import type { ComponentProps } from 'react'
import { useCallback, useReducer, useRef } from 'react'
import { TextInput } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useFocusEffect, useRouter } from 'expo-router'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { getVoiceTypeOptions } from '@/src/features/onboarding/screens/post-payment-onboarding/voice-type-options'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import { useToast } from '@/src/hooks/useToast'
import { useUpdateUserMutation } from '@/src/hooks/useUpdateUserMutation'
import i18n from '@/src/i18n'
import {
  customerInfoQueryOptions,
  deletionEligibilityQueryOptions,
  useDeleteAccount,
  useRestorePurchases,
} from '@/src/queries/account-queries'
import { useResendVerificationEmail } from '@/src/queries/auth-queries'
import { queryKeys } from '@/src/queries/query-keys'
import {
  useCreateCancelPortalSession,
  useCreatePortalSession,
} from '@/src/queries/stripe-queries'
import { userQueryOptions } from '@/src/queries/user-queries'
import { appQueryOptions } from '@/src/queries/view-queries'
import { showAlert } from '@/src/utils/alert'
import { toAbsoluteUrl } from '@/src/utils/url'
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { Translate } from './settingsScreenActions'
import { SettingsContent } from '../components/SettingsContent'
import { SettingsDialogs } from '../components/SettingsDialogs'
import {
  getErrorMessage,
  getSubscriptionStatus,
  getTimeRemaining,
  getUserDisplayName,
} from '../utils/settings-screen-helpers'
import {
  applyLanguageSelection,
  continueAfterUnsubscribe,
  deleteAccount,
  manageSubscription,
  openDeleteAccountDialog,
  openSettingsUrl,
  openUnsubscribeUrl,
  restorePurchases,
  saveEditedName,
  showLanguageChangeOptions,
} from './settingsScreenActions'
import {
  initialSettingsUiState,
  settingsUiReducer,
} from './settingsScreenState'

export function useSettingsScreenController() {
  const {
    user: userBack,
    getAccessToken,
    authUserId,
    getUserInfo,
  } = useResolvedAuthState()
  const { logout: authLogout, refreshUserStatus } = useAppSession()

  const queryClient = useQueryClient()
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const {
    data: backendUser,
    refetch: refetchUser,
    isPending: isPendingUser,
  } = useSuspenseQuery(userQueryOptions(authUserId, getAccessToken))
  const [uiState, dispatch] = useReducer(
    settingsUiReducer,
    initialSettingsUiState,
  )
  const {
    data: eligibility,
    refetch: refetchEligibility,
    isPending: isPendingEligibility,
  } = useSuspenseQuery(
    deletionEligibilityQueryOptions(authUserId, backendUser?.revenueCatId),
  )

  const { data: user, refetch: refetchUserInfo } = useQuery({
    queryKey: queryKeys.user.emailVerification.byUserId(authUserId),
    queryFn: async () => {
      const profile = await getUserInfo()
      return profile
    },
    placeholderData: userBack,
    enabled: !!authUserId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 0,
    gcTime: 0,
  })
  const assertValidUserEmailMutation = useMutation({
    mutationFn: async () => {
      const { data: user } = await refetchUserInfo()
      const emailVerified = user?.emailVerified ?? false
      if (!emailVerified) {
        throw new Error('User email is not verified')
      }
    },
    retry: (failureCount) => failureCount < 10,
    retryDelay: (failureCount) => Math.min(1000 * 2 ** failureCount, 30000),
  })

  const createPortalSession = useCreatePortalSession()
  const createCancelPortalSession = useCreateCancelPortalSession()
  const deleteAccountMutation = useDeleteAccount()
  const restorePurchasesMutation = useRestorePurchases(
    authUserId,
    backendUser?.revenueCatId,
  )
  const { t } = useTranslation(['profile', 'common', 'onboarding'])
  const { showToast } = useToast()
  const resendVerificationEmail = useResendVerificationEmail()
  const updateUserMutation = useUpdateUserMutation()
  const editNameInputRef = useRef<TextInput>(null)

  const displayName = getUserDisplayName({
    user,
    backendFirstName: backendUser?.firstName,
    fallbackName: t('profile:anonymous_user'),
  })
  const editNameTrimmed = uiState.editNameValue.trim()
  const editNameCanSave =
    editNameTrimmed.length > 0 &&
    editNameTrimmed !== (backendUser?.firstName ?? displayName) &&
    !updateUserMutation.isPending

  const logoutMutation = useMutation({
    mutationFn: () => authLogout(),
    onError: (error: unknown) => {
      const message = getErrorMessage(error) ?? t('profile:logout_failed')
      showAlert(t('common:error'), message)
    },
  })

  const locale = useContentfulLocale()
  const { data: app } = useSuspenseQuery(appQueryOptions(locale))
  const settingsPage = app.settingsPage
  const termsOfServiceText = settingsPage.termOfServiceText ?? ''
  const privacyPolicyText = settingsPage.privacyPolicyText ?? ''
  const termsOfServiceLink = toAbsoluteUrl(settingsPage.termOfServiceLink ?? '')
  const privacyPolicyLink = toAbsoluteUrl(settingsPage.privacyPolicyLink ?? '')

  const {
    data: customerInfo,
    refetch: refetchCustomerInfo,
    isPending: isCustomerInfoPending,
  } = useSuspenseQuery(customerInfoQueryOptions(authUserId))

  const subscriptionStatus = getSubscriptionStatus(
    customerInfo,
    backendUser?.revenueCatId,
    backendUser?.entitlement,
  )

  const isRefreshing =
    isPendingUser || isPendingEligibility || isCustomerInfoPending

  const handleRefresh = useCallback(() => {
    refetchUser()
    refetchEligibility()
    refreshUserStatus()
  }, [refetchEligibility, refetchUser, refreshUserStatus])

  useFocusEffect(handleRefresh)

  const handleLogout = useCallback(() => {
    if (logoutMutation.isPending) {
      return
    }
    logoutMutation.mutate()
  }, [logoutMutation])

  const handleOpenEditName = useCallback(() => {
    const prefill = backendUser?.firstName ?? displayName
    dispatch({ type: 'openEditNameDialog', value: prefill })
  }, [backendUser?.firstName, displayName])

  const currentLanguageLabel = i18n.language.startsWith('sv')
    ? t('profile:language_sv')
    : t('profile:language_en')
  const voiceTypeOptions = getVoiceTypeOptions(t)
  const currentVoiceTypeLabel =
    voiceTypeOptions.find((option) => option.key === backendUser?.voiceType)
      ?.label ?? ''
  const translate: Translate = (key, options) =>
    t(key as never, options as never) as unknown as string
  const handleApplyLanguageSelection = useCallback(
    (language: 'en' | 'sv') =>
      applyLanguageSelection(
        language,
        () => dispatch({ type: 'setShowLanguageOptions', value: false }),
        () => showAlert(t('common:error'), t('common:failed_to_save_language')),
      ),
    [t],
  )

  const remaining = getTimeRemaining(subscriptionStatus.expirationDate)
  const expirationText = !remaining
    ? t('profile:subscription_ends_soon')
    : t(`profile:subscription_ends_in_${remaining.unit}`, {
        count: remaining.count,
      })

  const avatarInitial = displayName.charAt(0).toUpperCase()
  const userEmail = user?.email ?? backendUser?.email ?? ''

  return {
    headTitle: t('common:page_title_settings'),
    topInset: insets.top,
    contentProps: {
      bottomInset: insets.bottom,
      onRefresh: handleRefresh,
      avatarInitial,
      displayName,
      userEmail,
      onOpenEditName: handleOpenEditName,
      onResendVerification: () => {
        resendVerificationEmail.mutate(undefined, {
          onSuccess: () => {
            dispatch({ type: 'setHasSentVerificationEmail', value: true })
            void assertValidUserEmailMutation.mutateAsync()
            showToast({
              variant: 'success',
              title: t('profile:resend_verification_success_title'),
              message: t('profile:resend_verification_success_message', {
                email: userEmail,
              }),
              autoDismiss: false,
            })
          },
          onError: () => {
            showAlert(
              t('common:error'),
              t('profile:resend_verification_failed'),
            )
          },
        })
      },
      onLogout: handleLogout,
      currentLanguageLabel,
      onLanguageChange: () =>
        showLanguageChangeOptions({
          t: translate,
          onShowWebModal: () =>
            dispatch({ type: 'setShowLanguageOptions', value: true }),
          onSelectLanguage: (language) => {
            void handleApplyLanguageSelection(language)
          },
        }),
      currentVoiceTypeLabel,
      onVoiceTypeChange: () =>
        dispatch({ type: 'setShowVoiceTypeOptions', value: true }),
      expirationText,
      onManageSubscription: () =>
        void manageSubscription({
          authUserId,
          queryClient,
          createPortalSession,
          dispatch,
          t: translate,
        }),
      onRestorePurchase: () =>
        restorePurchases({
          revenueCatId: backendUser?.revenueCatId,
          refreshUserStatus,
          refetchCustomerInfo,
          refetchUser,
          restorePurchasesMutation,
          t: translate,
        }),
      termsOfServiceText,
      privacyPolicyText,
      onOpenTermsOfService: () => void openSettingsUrl(termsOfServiceLink),
      onOpenPrivacyPolicy: () => void openSettingsUrl(privacyPolicyLink),
      onReportIssue: () => router.push('/report-issue'),
      onDeleteAccount: () =>
        void openDeleteAccountDialog({
          refetchEligibility,
          dispatch,
        }),
      status: {
        hasActiveSubscription: subscriptionStatus.isActive,
        hasSentVerificationEmail: uiState.hasSentVerificationEmail,
        isAssertingValidUserEmail: assertValidUserEmailMutation.isPending,
        isCustomerInfoPending,
        isEmailVerified: user?.emailVerified ?? false,
        isLogoutPending: logoutMutation.isPending,
        isManagingSubscription: uiState.isManagingSubscription,
        isPendingEligibility,
        isRefreshing,
        isResendVerificationPending: resendVerificationEmail.isPending,
        isRestorePending: restorePurchasesMutation.isPending,
        isSubscriptionCancelled: subscriptionStatus.isCancelled,
      },
    } satisfies ComponentProps<typeof SettingsContent>,
    dialogProps: {
      canSaveName: editNameCanSave,
      editNameError: uiState.editNameError,
      editNameInputRef,
      editNameValue: uiState.editNameValue,
      eligibleForDelete: eligibility?.eligible,
      isCheckingEligibility: uiState.isCheckingEligibility,
      isDeleting: deleteAccountMutation.isPending,
      isSavingName: updateUserMutation.isPending,
      language: i18n.language,
      selectedVoiceType: backendUser?.voiceType ?? null,
      voiceTypeOptions,
      onChangeEditName: (text: string) =>
        dispatch({ type: 'setEditNameValue', value: text }),
      onCloseEditName: () => dispatch({ type: 'closeEditNameDialog' }),
      onCloseLanguageOptions: () =>
        dispatch({ type: 'setShowLanguageOptions', value: false }),
      onCloseVoiceTypeOptions: () =>
        dispatch({ type: 'setShowVoiceTypeOptions', value: false }),
      onCloseRemoveAccount: () =>
        dispatch({ type: 'setShowRemoveAccountDialog', value: false }),
      onContinueAfterUnsubscribe: () =>
        void continueAfterUnsubscribe({
          dispatch,
          refetchEligibility,
          t: translate,
        }),
      onDeleteAccount: () =>
        void deleteAccount({
          deleteAccountMutation,
          authLogout,
          dispatch,
          t: translate,
        }),
      onOpenUnsubscribeLink: () =>
        void openUnsubscribeUrl({
          authUserId,
          queryClient,
          createCancelPortalSession,
          t: translate,
        }),
      onSaveName: () =>
        saveEditedName({
          editNameValue: uiState.editNameValue,
          dispatch,
          updateUserMutation: {
            isPending: updateUserMutation.isPending,
            mutate: (params) => {
              if (userEmail.length === 0) return
              return updateUserMutation.mutate({
                email: userEmail,
                firstName: params.firstName,
                voiceType: backendUser?.voiceType ?? null,
              })
            },
          },
          t: translate,
        }),
      onSelectLanguage: (language: 'en' | 'sv') =>
        void handleApplyLanguageSelection(language),
      onSelectVoiceType: (voiceType) => {
        if (userEmail.length === 0 || updateUserMutation.isPending) return

        updateUserMutation.mutate(
          {
            email: userEmail,
            firstName: backendUser?.firstName ?? null,
            voiceType,
          },
          {
            onSuccess: () =>
              dispatch({ type: 'setShowVoiceTypeOptions', value: false }),
            onError: () => {
              showAlert(t('common:error'))
            },
          },
        )
      },
      removeDialogStep: uiState.removeDialogStep,
      showEditNameDialog: uiState.showEditNameDialog,
      showLanguageOptions: uiState.showLanguageOptions,
      showVoiceTypeOptions: uiState.showVoiceTypeOptions,
      showRemoveAccountDialog: uiState.showRemoveAccountDialog,
    } satisfies ComponentProps<typeof SettingsDialogs>,
  }
}
